import { useQueryClient } from "@tanstack/react-query";

import type { Me } from "../../api/gen/model";
import { m } from "../../paraglide/messages.js";
import { type Locale, locales } from "../../paraglide/runtime.js";
import { useAction } from "../../useAction";
import { authClient } from "../auth/client";
import { useSession } from "../auth/session";
import { useLocale } from "./locale";

const names: Record<Locale, () => string> = { fr: m.language_fr, en: m.language_en };

const looks = {
  compact: {
    group: "flex gap-1 font-mono text-xs uppercase",
    option: "rounded-control px-2 py-1 pointer-coarse:min-h-11",
    chosen: "bg-graphite text-white",
    other: "text-text-muted hover:bg-surface",
  },
  chips: {
    group: "flex flex-wrap gap-2",
    option: "inline-flex min-h-11 items-center gap-2 rounded-control border px-4 text-graphite",
    chosen: "border-accent bg-accent-soft",
    other: "border-control-border bg-white hover:bg-surface",
  },
} as const;

type LanguageSwitchProps = {
  look?: keyof typeof looks;
};

/** Switches the language. For a signed-in person it is saved to the account first, so emails follow it. */
export function LanguageSwitch({ look = "compact" }: LanguageSwitchProps) {
  const { locale, change } = useLocale();
  const session = useSession();
  const queryClient = useQueryClient();
  const { busy, error, run } = useAction(async (next: Locale) => {
    if (session.status === "signedIn") {
      await (await authClient()).updateUser({ data: { locale: next } });
      // Asked for here: the switch is on the first screen, whose code stays free of the generated client.
      const { getGetMeQueryKey } = await import("../../api/gen/endpoints/me/me");
      queryClient.setQueryData<Me>(getGetMeQueryKey(), (me) => (me === undefined ? me : { ...me, locale: next }));
    }
    change(next);
  });
  const style = looks[look];
  return (
    <div className="flex flex-wrap items-center gap-2">
      {error === null ? null : (
        <span role="alert" className="text-xs text-danger">
          {error}
        </span>
      )}
      <fieldset className={style.group} disabled={busy}>
        <legend className="sr-only">{m.language_label()}</legend>
        {locales.map((option) => (
          <button
            key={option}
            type="button"
            lang={option}
            aria-pressed={option === locale}
            onClick={() => run(option)}
            className={`${style.option} ${option === locale ? style.chosen : style.other}`}
          >
            {look === "chips" && option === locale ? (
              <span aria-hidden="true" className="font-medium text-accent">
                ✓
              </span>
            ) : null}
            {names[option]()}
          </button>
        ))}
      </fieldset>
    </div>
  );
}
